export const ErrorDomain = {
  HTTP: 'http',
  JRPC: 'jrpc',
  AUTH: 'auth',
  BUSINESS: 'business',
  WEBSOCKET: 'websocket',
  GENERAL: 'general'
} as const;

export type ErrorDomain = (typeof ErrorDomain)[keyof typeof ErrorDomain];

export const ErrorCode = {
  // --- HTTP ---
  HTTP_REQUEST_FAILED: 'http_request_failed',
  HTTP_BAD_REQUEST: 'http_bad_request',
  HTTP_UNAUTHORIZED: 'http_unauthorized',
  HTTP_FORBIDDEN: 'http_forbidden',
  HTTP_NOT_FOUND: 'http_not_found',
  HTTP_INTERNAL_SERVER_ERROR: 'http_internal_server_error',
  HTTP_BAD_GATEWAY: 'http_bad_gateway',
  HTTP_SERVICE_UNAVAILABLE: 'http_service_unavailable',
  HTTP_GATEWAY_TIMEOUT: 'http_gateway_timeout',

  // --- JRPC ---
  JRPC_RESPONSE_ERROR: 'jrpc_response_error',

  // --- Auth ---
  AUTH_OKTA_CALLBACK_MISSING_CODE: 'auth_okta_callback_missing_code',
  AUTH_OKTA_CALLBACK_MISSING_REDIRECT_URI: 'auth_okta_callback_missing_redirect_uri',
  AUTH_OKTA_CALLBACK_MISSING_PENDING_GCS_ID: 'auth_okta_callback_missing_pending_gcs_id',
  AUTH_OKTA_CALLBACK_GCS_CONFIG_NOT_FOUND: 'auth_okta_callback_gcs_config_not_found',

  // --- WebSocket ---
  ABNORMAL_WEBSOCKET_CLOSURE: 'abnormal_websocket_closure',

  // --- Business ---
  GCS_NOT_FOUND: 'gcs_not_found',
  MISSION_NOT_FOUND: 'mission_not_found',

  // --- General ---
  NOT_IMPLEMENTED: 'not_implemented',
  SEND_LOGS: 'send_logs',
  UNKNOWN: 'unknown'
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export type ErrorParams = Record<string, string | number | boolean | null | undefined>;

export class AppError extends Error {
  public readonly domain: ErrorDomain;
  public readonly code: ErrorCode;
  public readonly params: ErrorParams;

  constructor(params: { domain: ErrorDomain; code: ErrorCode; params?: ErrorParams }) {
    super(params.code);
    this.domain = params.domain;
    this.code = params.code;
    this.params = params.params ?? {};

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function normalizeError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error;
  }

  if (error instanceof TypeError) {
    const message = error.message;
    if (message.toLowerCase().includes('failed to fetch')) {
      return new AppError({
        domain: ErrorDomain.HTTP,
        code: ErrorCode.HTTP_REQUEST_FAILED
      });
    }
  }

  if (error instanceof Response) {
    const response = error as Response;
    if (!response.ok) {
      const message: string = response.statusText;
      let code: ErrorCode;
      switch (response.status) {
        case 400:
          code = ErrorCode.HTTP_BAD_REQUEST;
          break;
        case 401:
          code = ErrorCode.HTTP_UNAUTHORIZED;
          break;
        case 403:
          code = ErrorCode.HTTP_FORBIDDEN;
          break;
        case 404:
          code = ErrorCode.HTTP_NOT_FOUND;
          break;
        case 500:
          code = ErrorCode.HTTP_INTERNAL_SERVER_ERROR;
          break;
        case 502:
          code = ErrorCode.HTTP_BAD_GATEWAY;
          break;
        case 503:
          code = ErrorCode.HTTP_SERVICE_UNAVAILABLE;
          break;
        case 504:
          code = ErrorCode.HTTP_GATEWAY_TIMEOUT;
          break;
        default:
          code = ErrorCode.UNKNOWN;
      }

      return new AppError({
        domain: ErrorDomain.HTTP,
        code,
        params: {
          statusCode: response.status,
          statusText: message || 'HTTP request failed'
        }
      });
    }
  }

  return new AppError({
    domain: ErrorDomain.GENERAL,
    code: ErrorCode.UNKNOWN
  });
}

export function parseSerializedAppError(value: string | null): AppError | undefined {
  if (!value) return undefined;

  try {
    const parsed = JSON.parse(value) as { domain?: unknown; code?: unknown };
    if (
      Object.values(ErrorDomain).includes(parsed.domain as ErrorDomain) &&
      Object.values(ErrorCode).includes(parsed.code as ErrorCode)
    ) {
      return new AppError({ domain: parsed.domain as ErrorDomain, code: parsed.code as ErrorCode });
    }
  } catch {
    return undefined;
  }

  return undefined;
}
